import React, { useEffect, useState, useCallback } from 'react';
import Axios from 'axios';
import config from '../../Config/key';
import {
  Typography,
  Select,
  Button,
  message,
  Card,
  Progress,
  Alert,
  Space,
  Divider,
  Tag,
  Spin,
  List,
} from 'antd';
import { InboxOutlined, FileZipOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import { StatusBadge } from '../../common';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// 50MB（バックエンドの multer 上限と一致させる）
const MAX_ZIP_SIZE = 50 * 1024 * 1024;

// 「記事アップロード」画面
// - シリーズを選択
// - MD + 画像を含む ZIP をドロップ or 選択
// - 「アップロード」ボタンでバックエンドに送信
// - 結果（成功 or エラー詳細）を画面に表示
function ArticleUploadPage(props) {
  const [seriesList, setSeriesList] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [selectedSeriesCode, setSelectedSeriesCode] = useState(undefined);

  const [zipFile, setZipFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // 結果（成功時のみ）
  const [result, setResult] = useState(null);
  // エラー（失敗時のみ）。バックエンドからの構造化エラー
  const [error, setError] = useState(null);

  // ─── シリーズ一覧取得 ───
  // アップロード画面は role 9 専用（Auth HOC でゲート済み）。記事が0件の空シリーズも
  // 選択できるよう role=9 を付与（未指定だとサーバーが「公開記事を持つシリーズ」のみに絞り込み、
  // 新規作成直後の空シリーズが一覧に出ない）
  const fetchSeries = useCallback(() => {
    setSeriesLoading(true);
    Axios.get('/api/articleSeries?role=9')
      .then((res) => {
        if (res.data && res.data.success) {
          setSeriesList(res.data.series || []);
        } else {
          message.error('シリーズ一覧の取得に失敗しました');
        }
      })
      .catch(() => message.error('シリーズ一覧の取得に失敗しました'))
      .finally(() => setSeriesLoading(false));
  }, []);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  // ─── ZIP ドロップ ───
  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      const r = rejectedFiles[0];
      // react-dropzone v11 互換
      const errMsg =
        (r.errors && r.errors[0] && r.errors[0].message) ||
        '無効なファイルです';
      message.error(`ファイルを受け付けられません: ${errMsg}`);
      return;
    }
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const f = acceptedFiles[0];
    if (f.size > MAX_ZIP_SIZE) {
      message.error(
        `ZIP サイズが上限（${MAX_ZIP_SIZE / 1024 / 1024}MB）を超えています`
      );
      return;
    }
    if (!f.name.toLowerCase().endsWith('.zip')) {
      message.error('ZIP ファイル（.zip）のみアップロード可能です');
      return;
    }
    setZipFile(f);
    setResult(null);
    setError(null);
  };

  // ─── アップロード送信 ───
  const onUpload = () => {
    if (!selectedSeriesCode) {
      message.warning('シリーズを選択してください');
      return;
    }
    if (!zipFile) {
      message.warning('ZIP ファイルを選択してください');
      return;
    }

    setUploading(true);
    setProgress(0);
    setResult(null);
    setError(null);

    const formData = new FormData();
    formData.append('zip', zipFile);
    formData.append('series_code', selectedSeriesCode);

    Axios.post('/api/article/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total) {
          setProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    })
      .then((res) => {
        if (res.data && res.data.success) {
          setResult({
            ...res.data.article,
            _thumbnail_warning: res.data.thumbnail_warning || null,
          });
          message.success('記事を登録しました');
          // フォームを部分的にリセット（シリーズ選択は維持）
          setZipFile(null);
          setProgress(100);
        } else {
          setError(res.data || { error: '登録に失敗しました' });
        }
      })
      .catch((err) => {
        const data = err.response && err.response.data;
        if (data) {
          setError(data);
        } else {
          setError({ error: err.message || 'ネットワークエラー' });
        }
      })
      .finally(() => {
        setUploading(false);
      });
  };

  // ─── レンダリング ───
  return (
    <div style={{ padding: '24px 32px', maxWidth: 880, margin: '0 auto' }}>
      <Title level={3}>記事アップロード</Title>
      <Paragraph type="secondary">
        MD ファイルと画像を ZIP にまとめて、対象シリーズにアップロードします。
        シリーズの作成・設定、frontmatter の書き方、画像・ZIP の作り方は{' '}
        <a
          href={
            config.ARTICLE_GUIDE_URL ||
            'https://re-current-doc.s3.ap-northeast-1.amazonaws.com/system_guide/item/article-guide/index.html'
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          「記事機能 利用ガイド」
        </a>{' '}
        を参照してください（新しいタブで開きます）。
      </Paragraph>

      <Divider />

      {/* シリーズ選択 */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>1. アップロード先シリーズ</Text>
        <div style={{ marginTop: 8 }}>
          {seriesLoading ? (
            <Spin />
          ) : (
            <Select
              showSearch
              placeholder="シリーズを選択..."
              value={selectedSeriesCode}
              onChange={setSelectedSeriesCode}
              style={{ width: '100%' }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option.children)
                  .toLowerCase()
                  .indexOf(String(input).toLowerCase()) >= 0
              }
            >
              {seriesList.map((s) => (
                <Option key={s.series_code} value={s.series_code}>
                  {s.series_name}（{s.series_code}）
                </Option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {/* ZIP ドロップ */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>2. ZIP ファイル</Text>
        <Dropzone
          onDrop={onDrop}
          multiple={false}
          maxSize={MAX_ZIP_SIZE}
          accept=".zip,application/zip,application/x-zip-compressed"
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              style={{
                marginTop: 8,
                padding: 32,
                border: `2px dashed ${isDragActive ? 'var(--blue-default)' : '#d9d9d9'}`,
                borderRadius: 6,
                textAlign: 'center',
                background: isDragActive ? '#e6f7ff' : '#fafafa',
                cursor: 'pointer',
              }}
            >
              <input {...getInputProps()} />
              <InboxOutlined
                style={{ fontSize: 36, color: 'var(--blue-default)', marginBottom: 8 }}
              />
              <div>
                {zipFile ? (
                  <Space>
                    <FileZipOutlined />
                    <Text strong>{zipFile.name}</Text>
                    <Text type="secondary">
                      ({(zipFile.size / 1024 / 1024).toFixed(2)} MB)
                    </Text>
                  </Space>
                ) : isDragActive ? (
                  <Text>ここにファイルをドロップ</Text>
                ) : (
                  <Text>ZIP ファイルをドラッグ＆ドロップ、またはクリックして選択</Text>
                )}
              </div>
              <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>
                上限 {MAX_ZIP_SIZE / 1024 / 1024} MB / 1 ファイルのみ
              </div>
            </div>
          )}
        </Dropzone>
      </div>

      {/* アップロードボタン */}
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          size="large"
          onClick={onUpload}
          loading={uploading}
          disabled={!selectedSeriesCode || !zipFile}
          block
        >
          アップロード
        </Button>
      </div>

      {/* 進捗 */}
      {uploading && (
        <div style={{ marginBottom: 16 }}>
          <Progress percent={progress} />
        </div>
      )}

      {/* 結果（成功） */}
      {result && (
        <Card
          style={{ marginTop: 16, borderColor: '#b7eb8f' }}
          title={
            <Space>
              <CheckCircleTwoTone twoToneColor="#52c41a" />
              <span>登録完了</span>
            </Space>
          }
        >
          {result._thumbnail_warning && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="サムネイル未設定"
              description={result._thumbnail_warning}
            />
          )}
          <List
            size="small"
            dataSource={[
              { k: '記事ID', v: result._id },
              { k: 'タイトル', v: result.title },
              { k: 'シリーズ', v: result.series_code },
              { k: '編', v: result.edition_name },
              {
                k: 'カテゴリ',
                v: result.category_name || '（カテゴリなし）',
              },
              { k: '著者', v: result.instructor_email },
              {
                k: '表示順（カテゴリ内）',
                v: String(result.position),
              },
              {
                k: '公開日',
                v: result.publish_date
                  ? String(result.publish_date).slice(0, 10)
                  : '',
              },
              {
                k: '添付画像',
                v: `${(result.attached_images || []).length} 枚`,
              },
            ]}
            renderItem={(item) => (
              <List.Item>
                <Text type="secondary" style={{ width: 160 }}>
                  {item.k}
                </Text>
                <Text>{item.v}</Text>
              </List.Item>
            )}
          />
          {(result.attached_images || []).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">画像URL:</Text>
              <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                {result.attached_images.map((img) => (
                  <li key={img.s3_key}>
                    <a
                      href={img.s3_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {img.original_name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* エラー */}
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message={error.error || 'アップロードに失敗しました'}
          description={
            <div>
              {error.field && (
                <div>
                  <Text type="secondary">問題のあるフィールド:</Text>{' '}
                  <StatusBadge tone="error">{error.field}</StatusBadge>
                </div>
              )}
              {error.input_value !== undefined && (
                <div>
                  <Text type="secondary">入力値:</Text>{' '}
                  <Text code>{String(error.input_value)}</Text>
                </div>
              )}
              {error.expected_value !== undefined && (
                <div>
                  <Text type="secondary">期待値:</Text>{' '}
                  <Text code>{String(error.expected_value)}</Text>
                </div>
              )}
              {Array.isArray(error.available_editions) && (
                <div>
                  <Text type="secondary">登録済みの 編:</Text>{' '}
                  {error.available_editions.length > 0
                    ? error.available_editions.map((n) => (
                        <Tag key={n}>{n}</Tag>
                      ))
                    : <Text type="secondary">（なし）</Text>}
                </div>
              )}
              {Array.isArray(error.available_categories) && (
                <div>
                  <Text type="secondary">登録済みの カテゴリ:</Text>{' '}
                  {error.available_categories.length > 0
                    ? error.available_categories.map((n) => (
                        <Tag key={n}>{n}</Tag>
                      ))
                    : <Text type="secondary">（なし）</Text>}
                </div>
              )}
              {Array.isArray(error.missing_fields) && (
                <div>
                  <Text type="secondary">不足しているフィールド:</Text>{' '}
                  {error.missing_fields.map((n) => (
                    <StatusBadge key={n} tone="warning">
                      {n}
                    </StatusBadge>
                  ))}
                </div>
              )}
              {Array.isArray(error.missing_images) && (
                <div>
                  <Text type="secondary">ZIP に欠けている画像:</Text>{' '}
                  {error.missing_images.map((n) => (
                    <StatusBadge key={n} tone="error">
                      {n}
                    </StatusBadge>
                  ))}
                </div>
              )}
              {Array.isArray(error.duplicate_filenames) && (
                <div>
                  <Text type="secondary">重複しているファイル名:</Text>{' '}
                  {error.duplicate_filenames.map((n) => (
                    <StatusBadge key={n} tone="error">
                      {n}
                    </StatusBadge>
                  ))}
                </div>
              )}
              {Array.isArray(error.available_images) && (
                <div>
                  <Text type="secondary">ZIP 内の画像ファイル:</Text>{' '}
                  {error.available_images.length > 0
                    ? error.available_images.map((n) => (
                        <Tag key={n}>{n}</Tag>
                      ))
                    : <Text type="secondary">（なし）</Text>}
                </div>
              )}
              {error.hint && (
                <div style={{ marginTop: 6 }}>
                  <Text type="secondary">ヒント:</Text> {error.hint}
                </div>
              )}
              {error.existing_article_id && (
                <div>
                  <Text type="secondary">既存記事 _id:</Text>{' '}
                  <Text code>{error.existing_article_id}</Text>
                </div>
              )}
              {(error.field === 'series' ||
                error.field === 'edited' ||
                error.field === 'category') && (
                <div style={{ marginTop: 8 }}>
                  <a href="/articleseries">→ シリーズ設定画面へ</a>
                </div>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}

export default ArticleUploadPage;
